/**
 * Convite de paciente existente (A052, decisão 7).
 *
 * Uma marca nunca vincula à força uma conta que já existe em outra marca —
 * nem pelo cadastro manual, nem pelo upload em massa. Ela convida: o e-mail
 * leva um link, e o vínculo só nasce quando a pessoa abre o link pela caixa
 * de entrada dela, passa pelo gate da marca e aceita. Juntar contas é ato da
 * dona da conta.
 *
 * O envio é por fila (`PENDING` → `SENT`), com disparo gradual: um upload de
 * centenas de linhas não pode virar uma rajada no provedor de e-mail.
 */
import { z } from 'zod';

export const DIAS_DE_VALIDADE_DO_CONVITE_DE_PACIENTE = 7;

export const PatientInviteStatusEnum = z.enum([
  /** Na fila, ainda não enviado. */
  'PENDING',
  /** E-mail enviado; aguardando a pessoa. */
  'SENT',
  'ACCEPTED',
  /** Cancelado pela marca antes do aceite. */
  'CANCELLED',
]);
export type PatientInviteStatus = z.infer<typeof PatientInviteStatusEnum>;

/**
 * `LISTA` (A062, fase E): convite por lista de nome e e-mail, feito por quem
 * administra o acesso. Diferente dos outros dois, vale também para quem
 * ainda não tem conta: o link no e-mail prova a caixa de entrada, e a conta
 * nasce ali, com a senha.
 *
 * `DEPENDENTE` (A062, fase E): a colaboradora convida alguém da família.
 * `invitedByAccountId` é a conta dela; o vínculo nasce como dependente.
 */
// `IMPORTACAO` é legado: o upload em massa saiu na A062 (fase G), mas
// convites antigos com essa origem continuam no banco e precisam ser lidos.
export const PatientInviteOrigemEnum = z.enum(['MANUAL', 'IMPORTACAO', 'LISTA', 'DEPENDENTE']);

export const PatientInviteSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  /** A marca que convida — sempre uma marca; mantenedora não convida paciente. */
  brandId: z.string().min(1),
  /** O token vai no e-mail; o banco guarda só o hash (como em `teamInvites`). */
  tokenHash: z.string(),
  status: PatientInviteStatusEnum.default('PENDING'),
  origem: PatientInviteOrigemEnum,
  invitedByAccountId: z.string(),
  /** Empresa da carteira, quando a linha do upload ou o cadastro a informou. */
  companyId: z.string().nullable().default(null),
  /** O nome que veio na lista (A062, fase E). Sugestão para o cadastro; a pessoa corrige. */
  nomeInformado: z.string().nullable().optional(),

  /** Fila: não enviar antes disto (backoff em falha). */
  enviarApos: z.coerce.date(),
  enviadoEm: z.coerce.date().nullable().default(null),
  tentativasDeEnvio: z.number().int().nonnegative().default(0),
  ultimoErroDeEnvio: z.string().nullable().default(null),

  expiresAt: z.coerce.date(),
  acceptedAt: z.coerce.date().nullable().default(null),
  acceptedByAccountId: z.string().nullable().default(null),
  cancelledAt: z.coerce.date().nullable().default(null),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type PatientInvite = z.infer<typeof PatientInviteSchema>;

export type StatusDeConviteDePaciente = PatientInviteStatus | 'EXPIRED';

/** `EXPIRED` é derivado, não gravado: o que vale é a data. */
export function statusDoConviteDePaciente(
  convite: Pick<PatientInvite, 'status' | 'expiresAt'>,
  agora = new Date()
): StatusDeConviteDePaciente {
  if (convite.status === 'ACCEPTED' || convite.status === 'CANCELLED') return convite.status;
  return convite.expiresAt.getTime() < agora.getTime() ? 'EXPIRED' : convite.status;
}
